var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useLayoutEffect, useRef, useState } from "react";
function extractCurrencySymbol(priceWithSymbol) {
    return priceWithSymbol.charAt(0);
}
function calculateTicketPriceWithFees(price, currencySymbol, fees, inclusiveFees) {
    if (!(fees === null || fees === void 0 ? void 0 : fees.length) || inclusiveFees === undefined) {
        return {
            price: price
        };
    }
    var priceNumber = parseFloat(price.replace(currencySymbol, ''));
    var feeNumber = fees.map(function (_a) {
        var feesValue = _a.feesValue;
        return parseFloat(feesValue);
    }).reduce(function (previous, current) { return previous + current; }, 0);
    var formattedFeesValue = "".concat(currencySymbol).concat(feeNumber.toFixed(2));
    if (inclusiveFees) {
        return {
            price: "".concat(currencySymbol).concat((priceNumber + feeNumber).toFixed(2)),
            feesValue: formattedFeesValue
        };
    }
    else {
        return {
            price: "".concat(currencySymbol).concat(priceNumber.toFixed(2)),
            feesValue: formattedFeesValue
        };
    }
}
var objOrArrToArr = function (x) { return Array.isArray(x) ? x : [x]; };
export function parseRawJson(rawJson) {
    return {
        performanceRef: rawJson.performanceRef,
        size: {
            width: 3 * parseInt(rawJson.width),
            height: 3 * parseInt(rawJson.height),
        },
        priceBands: Object.fromEntries(Object.entries(rawJson.priceBands)
            .map(function (_a) {
            var bandId = _a[0], band = _a[1];
            return ({
                id: bandId,
                color: band.color,
                concessions: extractConcessions(band),
                title: band.title,
            });
        })
            .map(function (band) { return [band.id, band]; })),
        seats: Object.fromEntries(Object.entries(rawJson.seats)
            .map(function (_a) {
            var seatId = _a[0], seat = _a[1];
            return ({
                id: seatId,
                block: seat.block,
                priceBandId: seat.priceBandId,
                x: parseInt(seat.x),
                y: parseInt(seat.y),
                state: seat.state,
                title: seat.title,
                number: seat.title.replace("^".concat(seat.row), ""),
                row: seat.row
            });
        })
            .map(function (seat) { return [seat.id, seat]; })),
        labels: Object.entries(rawJson.labels)
            .map(function (_a) {
            var labelId = _a[0], label = _a[1];
            return ({
                id: labelId,
                x: parseInt(label.x),
                y: parseInt(label.y),
                title: label.title,
                rotateAngle: parseFloat(label.rotateAngle)
            });
        }),
        lastUpdate: rawJson.lastUpdate
    };
}
export var useFetch = function (url) {
    var _a = React.useState(null), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        fetch(url)
            .then(function (res) { return res.json(); })
            .then(function (data) { return setData(data); });
    }, [url]);
    return data;
};
export var calculateSeatState = function (seat, priceBand, multiSelections, selection) {
    if (seat.state === '0') {
        return 'unavailable';
    }
    if (multiSelections.enabled) {
        if (multiSelections.seats.includes(seat.id)) {
            return 'selected';
        }
        else if (multiSelections.priceBand.id !== priceBand.id) {
            return 'unavailable';
        }
        else if ((selection === null || selection === void 0 ? void 0 : selection.status) === 'success') {
            return "unavailable";
        }
    }
    if (selection) {
        switch (selection.status) {
            case 'error':
                return 'error';
            case 'pending':
                return 'pending';
            case 'success':
                return 'selected';
        }
    }
    return 'available';
};
function extractConcessions(band) {
    var currencySymbol = extractCurrencySymbol(band.price);
    var fullPriceTicket = band.hideFullPrice ? [] : [__assign({ title: "Full Price", id: "0", minimumQty: band.minimumQty, description: band.description, feesTitle: band.feesTitle, inclusiveFees: band.inclusiveFees, isPromoCodeRequired: band.isPromoCodeRequired }, calculateTicketPriceWithFees(band.price, currencySymbol, band.fees, band.inclusiveFees))];
    return __spreadArray(__spreadArray([], fullPriceTicket, true), band.concessions.map(function (_a) {
        var title = _a.title, description = _a.description, price = _a.price, id = _a.id, minimumQty = _a.minimumQty, fees = _a.fees, inclusiveFees = _a.inclusiveFees, isPromoCodeRequired = _a.isPromoCodeRequired;
        return (__assign({ title: title, id: id.replace("".concat(band.id, "_"), ''), minimumQty: minimumQty, description: description, feeTitle: fees.map(function (_a) {
                var feesTitle = _a.feesTitle;
                return feesTitle;
            }).join(', '), inclusiveFees: inclusiveFees, isPromoCodeRequired: isPromoCodeRequired }, calculateTicketPriceWithFees(price, currencySymbol, fees, inclusiveFees)));
    }), true);
}
export function useStageDimensions() {
    var ref = useRef(null);
    var _a = useState({ width: 0, height: 0 }), dimensions = _a[0], setDimensions = _a[1];
    useLayoutEffect(function () {
        var _a, _b, _c;
        var current = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getClientRect({ skipTransform: true });
        setDimensions({ width: (_b = current === null || current === void 0 ? void 0 : current.width) !== null && _b !== void 0 ? _b : 0, height: (_c = current === null || current === void 0 ? void 0 : current.height) !== null && _c !== void 0 ? _c : 0 });
    }, [ref.current]);
    return [ref, dimensions];
}
export function useDivDimensions() {
    var ref = useRef(null);
    var _a = useState({ width: 0, height: 100 }), dimensions = _a[0], setDimensions = _a[1];
    useLayoutEffect(function () {
        var _a, _b;
        var current = ref.current;
        setDimensions({ width: (_a = current === null || current === void 0 ? void 0 : current.offsetWidth) !== null && _a !== void 0 ? _a : 100, height: (_b = current === null || current === void 0 ? void 0 : current.offsetHeight) !== null && _b !== void 0 ? _b : 100 });
    }, [ref.current]);
    return [ref, dimensions];
}
export function calculateScaleToFitWithPadding(seatmapSize, containerSize, currentScale, padding) {
    var scaleToFitWidth = (containerSize.width === 0) ? 1 : (containerSize.width / ((seatmapSize.width / currentScale) + 2 * padding));
    var scaleToFitHeight = (containerSize.height === 0) ? 1 : (containerSize.height / ((seatmapSize.height / currentScale) + 2 * padding));
    return Math.round(Math.min(scaleToFitWidth, scaleToFitHeight) * 1000) / 1000;
}
export var SEAT_RADIUS = 3.6;
export var SEATMAP_PADDING = 10;
